import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Avatar, styled } from '@mui/material';
import AppBar from '@mui/material/AppBar';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Toolbar from '@mui/material/Toolbar';
import { CiSettings } from 'react-icons/ci';
import { HiOutlineUser } from 'react-icons/hi';
import { HiOutlineMenuAlt3, HiX } from 'react-icons/hi';
import { useDispatch, useSelector } from 'react-redux';
import { LogoutThunk } from '../../RTK/Thunk/LogoutThunk';
import { ArrowDown, Logout } from '../../assets/icons';
import { UserImage } from '../../assets/images';
import '../../index.css';

const AppBarsStyles = {
	'&.MuiPaper-root': {
		height: '70px',
		boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
		backgroundColor: '#59104c',
		zIndex: 1000,
	},
};

const ToolbarStyles = {
	'&.MuiToolbar-root': {
		paddingRight: '16px',
		paddingLeft: '16px',
		minHeight: '70px',
		justifyContent: 'space-between',
	},
};

const muiItemStyles = {
	'&.MuiMenuItem-root': {
		height: '48px',
		fontFamily: 'Tajawal',
		fontSize: '16px',
		fontWeight: 500,
		color: '#FFF5F8',
		transition: 'all 0.2s ease-in-out',
		'&:hover': {
			backgroundColor: '#E0A6B8',
			color: '#fff',
			transform: 'translateX(-2px)',
		},
	},
};

const StyledBadge = styled(Badge)(({ theme }) => ({
	'& .MuiBadge-badge': {
		backgroundColor: '#44b700',
		color: '#44b700',
		boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
		'&::after': {
			position: 'absolute',
			top: 0,
			left: 0,
			width: '100%',
			height: '100%',
			borderRadius: '50%',
			animation: 'ripple 1.5s infinite ease-in-out',
			border: '1px solid currentColor',
			content: '""',
		},
	},
	'@keyframes ripple': {
		'0%': {
			transform: 'scale(.8)',
			opacity: 1,
		},
		'100%': {
			transform: 'scale(2.4)',
			opacity: 0,
		},
	},
}));

const TopBar = ({ toggleSidebar, isMobile }) => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const userName = localStorage.getItem('userName');
	const { myAccountData } = useSelector(state => state.MyAccountReducer);
	const [userImage, setUserImage] = useState(localStorage.getItem('avatar'));
	const [anchorEl, setAnchorEl] = useState(null);
	const [sidebarOpen, setSidebarOpen] = useState(false);

	const isMenuOpen = Boolean(anchorEl);

	useEffect(() => {
		if (localStorage.getItem('avatar')) {
			setUserImage(localStorage.getItem('avatar'));
		}
	}, [myAccountData]);

	const handleProfileMenuOpen = event => {
		setAnchorEl(event.currentTarget);
	};

	const handleMenuClose = () => {
		setAnchorEl(null);
	};

	const handleSidebarToggle = () => {
		setSidebarOpen(!sidebarOpen);
		toggleSidebar();
	};

	const logout = () => {
		dispatch(LogoutThunk())
			.unwrap()
			.then(() => {
				localStorage.clear();
				navigate('/login');
			})
			.catch(error => {
				console.error('Logout failed:', error);
			});
	};

	const menuId = 'primary-search-account-menu';
	const renderMenu = (
		<Menu
			sx={{
				'& .MuiPaper-root': {
					width: '200px',
					backgroundColor: '#E0A6B8',
					top: '70px !important',
					boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
					borderRadius: '8px',
				},
				'& .MuiList-padding': {
					paddingTop: '8px',
					paddingBottom: '8px',
				},
			}}
			anchorEl={anchorEl}
			anchorOrigin={{
				vertical: 'top',
				horizontal: 'right',
			}}
			id={menuId}
			keepMounted
			transformOrigin={{
				vertical: 'top',
				horizontal: 'right',
			}}
			open={isMenuOpen}
			onClose={handleMenuClose}>
			<MenuItem
				className='group text-unnamed_color_C6C6C6'
				onClick={() => {
					handleMenuClose();
					navigate('/edit-my-account');
				}}
				sx={muiItemStyles}>
				<HiOutlineUser className='w-5 h-5 group-hover:text-white text-unnamed_color_f6f6f6 ml-3' />
				حسابي
			</MenuItem>
			<MenuItem
				className='group text-unnamed_color_C6C6C6'
				onClick={() => {
					handleMenuClose();
					navigate('/setting-my-account');
				}}
				sx={muiItemStyles}>
				<CiSettings className='w-5 h-5 group-hover:text-white text-unnamed_color_f6f6f6 ml-3' />
				الإعدادات
			</MenuItem>
		</Menu>
	);

	return (
		<Box sx={{ flexGrow: 1 }}>
			<AppBar position='fixed' sx={AppBarsStyles}>
				<Box className='w-full'>
					<Toolbar sx={ToolbarStyles}>
						{/* Left side - Burger menu for mobile */}
						<Box className='flex items-center gap-4'>
							{isMobile && (
								<IconButton
									onClick={handleSidebarToggle}
									className='text-white hover:bg-white/10 transition-colors'
									sx={{
										'&.MuiIconButton-root': {
											color: 'white',
											'&:hover': {
												backgroundColor: 'rgba(255, 255, 255, 0.1)',
											},
										},
									}}>
									{sidebarOpen ? (
										<HiX className='w-6 h-6' />
									) : (
										<HiOutlineMenuAlt3 className='w-6 h-6' />
									)}
								</IconButton>
							)}

							{/* Logo/Title */}
							<div className='hidden md:block'>
								<h1 className='text-white font-bold text-lg'>
									نظام إدارة المحتوى
								</h1>
							</div>
						</Box>

						{/* Right side - User info and logout */}
						<Box className='flex items-center gap-2 md:gap-4'>
							{/* User Profile */}
							<IconButton
								sx={{
									display: 'flex',
									gap: '8px',
									padding: '8px',
									borderRadius: '12px',
									transition: 'all 0.2s ease-in-out',
									'&.MuiIconButton-root': {
										'&:hover': {
											backgroundColor: 'rgba(255, 255, 255, 0.1)',
											transform: 'translateY(-1px)',
										},
									},
								}}
								size='large'
								edge='end'
								aria-label='account of current user'
								aria-controls={menuId}
								aria-haspopup='true'
								onClick={handleProfileMenuOpen}
								color='inherit'>
								<StyledBadge
									overlap='circular'
									anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
									variant='dot'>
									<Avatar
										alt='user name'
										src={userImage ? userImage : UserImage}
										sx={{
											width: { xs: 32, md: 40 },
											height: { xs: 32, md: 40 },
											border: '2px solid rgba(255, 255, 255, 0.2)',
										}}
									/>
								</StyledBadge>

								<Box className='hidden md:flex justify-center items-center gap-2'>
									<p className='text-sm md:text-base text-white font-medium font-Tajawal'>
										{userName || 'جمعية إضاءة المسرح'}
									</p>
									<ArrowDown className='w-4 h-4' />
								</Box>
							</IconButton>

							{/* Logout Button */}
							<IconButton
								onClick={logout}
								size='large'
								color='inherit'
								className='hover:bg-white/10 transition-colors'
								sx={{
									borderRadius: '8px',
									'&.MuiIconButton-root': {
										'&:hover': {
											backgroundColor: 'rgba(255, 255, 255, 0.1)',
											transform: 'translateY(-1px)',
										},
									},
								}}
								title='تسجيل الخروج'>
								<Logout className='w-5 h-5' />
							</IconButton>
						</Box>
					</Toolbar>
				</Box>
			</AppBar>

			{renderMenu}
		</Box>
	);
};

export default TopBar;
